import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { CloudUpload, Description, Delete, Schedule } from '@mui/icons-material'
import { StatusChip } from '@components/atoms'
import styles from '../SettingsSOP.module.scss'

const SOPFileCard = ({
  isAdmin,
  campaigns,
  selectedCampaign,
  handleCampaignChange,
  loading,
  sopExists,
  uploadDate = null,
  getCurrentCampaign,
  selectedFile = null,
  uploading,
  deleting,
  handleFileSelect,
  handleRemoveFile,
  handleUploadConfirm,
  setReuploadConfirmDialog,
  setDeleteConfirmDialog
}) => {
  if (loading) return null

  return (
    <Box className='uploadForm'>
      <Card className={styles.sopCard}>
        <CardContent>
          {/* Admin campaign dropdown at the top */}
          {isAdmin && (
            <Box mb={4}>
              <Typography variant='subtitle1' className={styles.sectionLabel}>
                Campaign Management
              </Typography>
              <FormControl fullWidth variant='outlined' size='small'>
                <InputLabel>Select campaign to manage</InputLabel>
                <Select
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  label='Select campaign to manage'
                  className={styles.selectRounded}
                >
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.original_name} value={campaign.original_name}>
                      {campaign.campaign_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* SOP File Status Title and rest of card */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            className={styles.statusRow}
          >
            <Typography variant='h6' className={styles.sectionTitle}>
              SOP File Status
            </Typography>
            {sopExists ? (
              <StatusChip label='Active' color='success' />
            ) : (
              <StatusChip label='Not Uploaded' color='neutral' />
            )}
          </Box>

          <Divider className={styles.divider} />

          <Box display='flex' alignItems='center' className={styles.infoRowSpacing}>
            <Description className={styles.iconLarge} />
            <Box>
              <Typography variant='body1' className={styles.fontMedium}>
                {sopExists ? 'SOP File Available' : 'No SOP File Found'}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {sopExists
                  ? `Campaign: ${decodeURIComponent(getCurrentCampaign() || '')}`
                  : 'Upload an SVG file to get started'}
              </Typography>
              {sopExists && uploadDate && (
                <Box display='flex' alignItems='center' className={styles.metaRow}>
                  <Schedule className={styles.iconSmall} />
                  <Typography variant='caption' color='textSecondary'>
                    Last updated: {new Date(uploadDate).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* File Upload Area */}
          <Box className={styles.uploadZone}>
            {!sopExists ? (
              <Box textAlign='center'>
                <CloudUpload className={styles.uploadIcon} />
                <Typography variant='h6' className={styles.uploadTitle}>
                  Upload SOP File
                </Typography>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className={styles.uploadDescription}
                >
                  Select an SVG file (max 5MB) to upload for this campaign
                </Typography>

                <input
                  id='file-input'
                  type='file'
                  accept='.svg,image/svg+xml'
                  onChange={handleFileSelect}
                  className={styles.fileInputHidden}
                />

                {!selectedFile ? (
                  <Button
                    variant='contained'
                    onClick={() => document.getElementById('file-input').click()}
                    className={styles.actionButton}
                    startIcon={<CloudUpload />}
                  >
                    Choose File
                  </Button>
                ) : (
                  <Box>
                    <Box className={styles.filePreview}>
                      <Box display='flex' alignItems='center'>
                        <Description className={styles.filePreviewIcon} />
                        <Box>
                          <Typography variant='body1' className={styles.filePreviewName}>
                            {selectedFile.name}
                          </Typography>
                          <Typography variant='caption' className={styles.filePreviewSize}>
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton onClick={handleRemoveFile} className={styles.removeFileButton}>
                        <Delete />
                      </IconButton>
                    </Box>

                    {uploading && (
                      <Box className={styles.progressWrapper}>
                        <LinearProgress variant='indeterminate' className={styles.progressBar} />
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          className={styles.progressLabel}
                        >
                          Uploading file...
                        </Typography>
                      </Box>
                    )}

                    <Button
                      variant='contained'
                      onClick={handleUploadConfirm}
                      disabled={!selectedFile || uploading}
                      className={styles.actionButton}
                      style={!selectedFile || uploading ? { backgroundColor: '#ccc' } : undefined}
                      startIcon={<CloudUpload />}
                    >
                      {uploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className={styles.existingFileMessage}
                >
                  SOP file is uploaded and ready to use
                </Typography>

                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      const campaign = encodeURIComponent(getCurrentCampaign())
                      window.location.href = `/sop/${campaign}`
                    }}
                    className={styles.viewButton}
                  >
                    View SOP
                  </Button>
                  <Button
                    variant='contained'
                    onClick={() => setReuploadConfirmDialog(true)}
                    disabled={deleting}
                    className={styles.actionButtonSmall}
                    startIcon={<CloudUpload />}
                  >
                    Reupload
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => setDeleteConfirmDialog(true)}
                    disabled={deleting}
                    className={styles.deleteButton}
                    startIcon={<Delete />}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

SOPFileCard.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      original_name: PropTypes.string.isRequired,
      campaign_name: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedCampaign: PropTypes.string.isRequired,
  handleCampaignChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  sopExists: PropTypes.bool.isRequired,
  uploadDate: PropTypes.string,
  getCurrentCampaign: PropTypes.func.isRequired,
  selectedFile: PropTypes.object,
  uploading: PropTypes.bool.isRequired,
  deleting: PropTypes.bool.isRequired,
  handleFileSelect: PropTypes.func.isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
  handleUploadConfirm: PropTypes.func.isRequired,
  setReuploadConfirmDialog: PropTypes.func.isRequired,
  setDeleteConfirmDialog: PropTypes.func.isRequired
}

export default SOPFileCard
